import {useEffect, useRef} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group';
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group';
import {useEntitledForClient} from '@/hooks/useEntitlements';
import JobRoleFieldLabel from '@/shared/job-roles/components/JobRoleFieldLabel';
import {JobRoleMultiInput} from '@/shared/job-roles/components/JobRoleInput';
import ResidentInput from '@/shared/residents/components/ResidentInput';
import UserInput from '@/shared/users/components/UserInput';
import {cn} from '@/utils/ui';

import type {TaskFormValues} from '../utils/tasksFormInputs';
import {StandardTaskVariant, TaskType} from '../utils/tasksFormInputs';
import OneTimeTaskFormFields from './task-form-fields/OneTimeTaskFormFields';
import RecurringTaskFormFields from './task-form-fields/RecurringTaskFields';

export type TaskFormFieldsProps = {
  form: UseFormReturn<TaskFormValues>;
  hideTaskType?: boolean;
  isStartDateLocked?: boolean;
};

const TaskFormFields = ({
  form,
  hideTaskType: hideStandardTaskVariant,
  isStartDateLocked,
}: TaskFormFieldsProps) => {
  const {t} = useTranslation();
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const isWorkScheduleEntitled = useEntitledForClient('WORK_SCHEDULE');

  const contentText = form.watch('content');

  useEffect(() => {
    if (!contentRef.current) return;

    contentRef.current.style.height = 'auto';
    contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
  }, [contentText]);

  return (
    <>
      <FormRequiredLegend />

      <FormField
        control={form.control}
        name="taskType"
        render={({field}) => (
          <FormItem>
            <FormControl>
              <ToggleGroup
                type="single"
                onValueChange={value => {
                  if (!value) return;

                  field.onChange(value);
                }}
                value={field.value}
                className="w-full justify-center bg-white"
                itemVariant="white"
              >
                <ToggleGroupItem className="grow" value={TaskType.Standard}>
                  {t('Task')}
                </ToggleGroupItem>
                <ToggleGroupItem className="grow" value={TaskType.Announcement}>
                  {t('Announcement')}
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="title"
        render={({field}) => (
          <FormItem>
            <FormLabel required className="sr-only">
              {t('Title')}
            </FormLabel>
            <FormControl>
              <input
                {...field}
                required
                placeholder={`${t('Title')} *`}
                className={cn(
                  'w-full text-lg font-semibold placeholder-gray-800',
                  'focus:outline-none'
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="content"
        render={({field}) => (
          <FormItem>
            <FormLabel className="sr-only">{t('Description')}</FormLabel>
            <FormControl>
              <textarea
                {...field}
                ref={contentRef}
                placeholder={t('Description')}
                className={cn(
                  'h-auto w-full placeholder-gray-700',
                  'focus:outline-none'
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="flex gap-4 max-sm:flex-col">
        <FormField
          control={form.control}
          name="assignedToResident"
          render={({field}) => (
            <FormItem className="grow basis-0">
              <FormLabel>{t('Resident')}</FormLabel>
              <FormControl>
                <ResidentInput
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="assignedToEmployee"
          render={({field}) => (
            <FormItem className="grow basis-0">
              <FormLabel>{t('Employee')}</FormLabel>
              <FormControl>
                <UserInput
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      {isWorkScheduleEntitled && (
        <FormField
          control={form.control}
          name="jobRoleIds"
          render={({field}) => (
            <FormItem>
              <JobRoleFieldLabel label={t('Visible only to job roles')} />
              <FormControl>
                <JobRoleMultiInput
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormDescription>
                {t('Leave empty to show the task to everyone.')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {!hideStandardTaskVariant && (
        <FormField
          control={form.control}
          name="standardTaskVariant"
          render={({field}) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  className="my-4 flex flex-wrap gap-2"
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <RadioGroupItem value={StandardTaskVariant.OneTime} />
                    </FormControl>
                    <FormLabel>{t('One time')}</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <RadioGroupItem value={StandardTaskVariant.Recurring} />
                    </FormControl>
                    <FormLabel>{t('Recurring')}</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
      )}
      {form.watch('standardTaskVariant') === StandardTaskVariant.OneTime && (
        <OneTimeTaskFormFields form={form} />
      )}
      {form.watch('standardTaskVariant') === StandardTaskVariant.Recurring && (
        <RecurringTaskFormFields
          form={form}
          isStartDateLocked={isStartDateLocked}
        />
      )}
    </>
  );
};

export default TaskFormFields;
